import logger from '@ayocore/platform/common/helpers/logger';
import type { AwaitableRpcClient } from '@ayocore/platform/server/rpc/types';
import type { ActivityChatPin } from '@ayocore/activity/common/types/activity-pin';
import type {
  ActivityPinRepository,
  ActivityThreadOpenTask,
} from '@ayocore/activity/server/repositories/ActivityPinRepository';
import { ControllerStore } from './ControllerStore';
import { INITIAL_PINS_STATE, type ActivityPinsState } from './types';

/**
 * ActivityPinsController — загрузка и операции по закрепам текущего треда.
 */
export class ActivityPinsController extends ControllerStore<ActivityPinsState> {
  private pinRepository: AwaitableRpcClient<ActivityPinRepository>;

  private pinsReloadTimer: ReturnType<typeof setTimeout> | null = null;

  private pinsRequestSeq = 0;

  private activeThreadId: number | null = null;

  constructor(pinRepository: AwaitableRpcClient<ActivityPinRepository>) {
    super(INITIAL_PINS_STATE);
    this.pinRepository = pinRepository;
  }

  /**
   * Обновляет ссылку на pinRepository при смене rpc-ref.
   */
  updatePinRepository(nextRepository: AwaitableRpcClient<ActivityPinRepository>): void {
    this.pinRepository = nextRepository;
  }

  /**
   * Инвалидирует pending pins-запросы и очищает debounce таймер.
   */
  invalidatePending(): void {
    this.pinsRequestSeq += 1;
    if (this.pinsReloadTimer) {
      clearTimeout(this.pinsReloadTimer);
      this.pinsReloadTimer = null;
    }
  }

  /**
   * Полностью очищает pins-state текущего треда.
   */
  clearPins(): void {
    this.activeThreadId = null;
    if (this.pinsReloadTimer) {
      clearTimeout(this.pinsReloadTimer);
      this.pinsReloadTimer = null;
    }
    this.setState({
      pins: [],
      pinsLoading: false,
      pinsErrorMessage: null,
    });
  }

  /**
   * Загружает закрепы треда и вычисляемые task pins.
   */
  async loadPins(threadId: number | null, options?: { showLoader?: boolean }): Promise<void> {
    const showLoader = options?.showLoader ?? true;

    this.activeThreadId = threadId;
    if (!threadId || threadId <= 0) {
      this.setState({
        pins: [],
        pinsLoading: false,
        pinsErrorMessage: null,
      });
      return;
    }

    const requestId = ++this.pinsRequestSeq;
    try {
      logger.debug('[Activity:Pins] loadPins started', { threadId, showLoader });

      if (showLoader) {
        this.setState({
          pinsLoading: true,
          pinsErrorMessage: null,
        });
      }

      const sharedPinsRaw = await this.pinRepository.getThreadPins(threadId);
      const sharedPins = Array.isArray(sharedPinsRaw) ? sharedPinsRaw : [];

      const openTasksRaw = await this.pinRepository.getThreadOpenTasks(threadId);
      const openTasks = Array.isArray(openTasksRaw) ? openTasksRaw : [];
      const taskPins = openTasks.map(task => this.mapTaskToPin(task));

      if (this.isPinsRequestStale(requestId, threadId)) return;

      const pins = [...sharedPins, ...taskPins];
      this.setState({
        pins,
        pinsErrorMessage: null,
      });
      logger.debug('[Activity:Pins] loadPins completed', { threadId, pinsCount: pins.length });
    } catch (error) {
      if (this.isPinsRequestStale(requestId, threadId)) return;
      logger.error('[Activity:Pins] loadPins failed', { threadId, error });
      this.setState({
        pins: [],
        pinsErrorMessage: error instanceof Error ? error.message : 'Не удалось загрузить закрепы.',
      });
    } finally {
      if (!this.isPinsRequestStale(requestId, threadId)) {
        this.setState({
          pinsLoading: false,
        });
      }
    }
  }

  /**
   * Планирует debounce-перезагрузку закрепов после realtime-сигнала.
   */
  schedulePinsReload(threadId: number | null, debounceMs: number = 150): void {
    if (this.pinsReloadTimer) {
      clearTimeout(this.pinsReloadTimer);
    }

    this.pinsReloadTimer = setTimeout(() => {
      if (!threadId || threadId <= 0) return;
      if (this.activeThreadId !== threadId) return;
      void this.loadPins(threadId, { showLoader: false });
    }, debounceMs);
  }

  /**
   * Закрепляет комментарий в выбранном треде.
   */
  async pinComment(threadId: number | null, commentUuid: string): Promise<void> {
    const normalizedCommentUuid = commentUuid?.trim();
    if (!threadId || threadId <= 0 || !normalizedCommentUuid) return;

    await this.pinRepository.pinComment(threadId, normalizedCommentUuid);
    await this.loadPins(threadId, { showLoader: false });
  }

  /**
   * Открепляет комментарий в выбранном треде.
   */
  async unpinComment(threadId: number | null, commentUuid: string): Promise<void> {
    const normalizedCommentUuid = commentUuid?.trim();
    if (!threadId || threadId <= 0 || !normalizedCommentUuid) return;

    await this.pinRepository.unpinComment(threadId, normalizedCommentUuid);
    await this.loadPins(threadId, { showLoader: false });
  }

  /**
   * Освобождает ресурсы контроллера закрепов.
   */
  dispose(): void {
    if (this.pinsReloadTimer) {
      clearTimeout(this.pinsReloadTimer);
      this.pinsReloadTimer = null;
    }
    super.dispose();
  }

  private isPinsRequestStale(requestId: number, threadId: number): boolean {
    return requestId !== this.pinsRequestSeq || this.activeThreadId !== threadId;
  }

  private formatDueLabel(
    dueDate: Date | string | null | undefined,
    dueTime: string | null | undefined
  ): string | undefined {
    const parts: string[] = [];
    if (dueDate) {
      const parsedDate = dueDate instanceof Date ? dueDate : new Date(dueDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        parts.push(parsedDate.toLocaleDateString('ru-RU'));
      }
    }
    if (typeof dueTime === 'string' && dueTime.trim()) {
      const time = dueTime.trim();
      parts.push(time.length > 5 ? time.slice(0, 5) : time);
    }
    return parts.length > 0 ? parts.join(' ') : undefined;
  }

  private getTaskPinOverdueColor(overdueDays: number | null | undefined): string | undefined {
    if (typeof overdueDays !== 'number' || !Number.isFinite(overdueDays) || overdueDays <= 0) {
      return undefined;
    }
    if (overdueDays <= 2) return '#FFFFFF';
    if (overdueDays <= 7) return '#FEECEC';
    if (overdueDays <= 14) return '#FDE2E2';
    return '#FBD5D5';
  }

  private mapTaskToPin(task: ActivityThreadOpenTask): ActivityChatPin {
    const dueLabel = this.formatDueLabel(task.due_date, task.due_time);
    const assigneeName = task.assignee_name || task.assignee_email || null;
    const overdueColor = this.getTaskPinOverdueColor(task.overdue_days);
    const taskPrefix = `#${task.id}`;
    const taskText = typeof task.title === 'string' ? task.title.trim() : '';
    const titleParts = [taskPrefix];
    if (taskText) titleParts.push(taskText);
    if (dueLabel) titleParts.push(dueLabel);
    const taskTitle = titleParts.join(' ');
    const prefixBadge =
      task.overdue_days > 0
        ? { text: String(task.overdue_days), variant: 'overdue' as const }
        : undefined;
    const subtitleParts: string[] = [];
    if (assigneeName) subtitleParts.push(`Кому ${assigneeName}`);

    return {
      id: `task:${task.id}`,
      kind: 'task',
      title: taskTitle,
      ...(prefixBadge ? { prefix_badge: prefixBadge } : {}),
      subtitle: subtitleParts.length > 0 ? subtitleParts.join(' ') : undefined,
      ...(overdueColor ? { color: overdueColor } : {}),
      due_at: task.due_date ?? null,
      assignee_name: assigneeName,
      payload: {
        task_id: task.id,
        task_uuid: task.uuid,
        task_type_id: task.task_type_id,
        entity_type: task.entity_type,
        entity_uuid: task.entity_uuid,
        scope_label: task.scope_label,
      },
      on_click_action: 'open_task_view',
      actions: [
        { id: 'reply_task', label: 'Ответить' },
        { id: 'edit_task', label: 'Редактировать' },
        { id: 'complete_task', label: 'Завершить' },
        { id: 'delete_task', label: 'Удалить', color: 'red' },
      ],
    };
  }
}
